import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Building2, CheckCircle, XCircle, Clock, TrendingUp, Users, LogOut, FileText, Loader2, Printer, MapPin } from 'lucide-react';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import ApprovalQueue from '@/components/ApprovalQueue';
import Login from '@/components/Login';
import AdminUsers from '@/components/AdminUsers';
import RadiusSearchInline from '@/components/RadiusSearchInline';
import LandingPage from './pages/LandingPage';
import ApprovedList from './pages/ApprovedList';
import PendingList from './pages/PendingList';
import RejectedList from './pages/RejectedList';
import ContactMessages from './pages/ContactMessages';
import BlacklistAdmin from './pages/BlacklistAdmin';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard' or 'admin-users'
  const [selectedList, setSelectedList] = useState(null); // null, 'approved', 'pending', 'rejected'
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState({ total: 0, approved: 0, rejected: 0, pending: 0 });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('Logistik');
  const [multiQuery, setMultiQuery] = useState(false);
  const [radius, setRadius] = useState(0); // Default 0 = city-only search (no radius)
  const [systemStatus, setSystemStatus] = useState(null);
  
  // Search progress stats (live counter) - persisted in localStorage
  const [searchStats, setSearchStats] = useState(() => {
    const saved = localStorage.getItem('searchStats');
    return saved ? JSON.parse(saved) : { found: 0, filtered: 0, approved: 0 };
  });

  // Setup axios interceptors inside component
  useEffect(() => {
    // Request interceptor for authentication
    const requestInterceptor = axios.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Response interceptor for 401 errors
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setCurrentUser(null); // Use state instead of reload
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptors on unmount
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  useEffect(() => {
    // Check if user is logged in
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setCurrentUser(JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    if (currentUser && currentView === 'dashboard') {
      fetchCompanies();
      fetchStats();
      fetchSystemStatus();
      // Refresh every 10 seconds
      const interval = setInterval(() => {
        fetchCompanies();
        fetchStats();
        fetchSystemStatus();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [currentUser, currentView]);

  const handleLogin = (user) => {
    setCurrentUser(user);
  };

  const fetchCompanies = async () => {
    try {
      console.log('📊 Fetching companies...');
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
      console.log('✅ Companies loaded:', response.data.length);
    } catch (error) {
      console.error('❌ Unternehmen konnten nicht geladen werden:', error.response?.status, error.message);
      if (error.response?.status === 401) {
        console.log('🔴 Unauthorized - logging out');
        handleLogout();
      }
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Statistiken konnten nicht geladen werden:', error);
    }
  };

  const fetchSystemStatus = async () => {
    try {
      const response = await axios.get(`${API}/system-status`);
      setSystemStatus(response.data);
    } catch (error) {
      console.error('Systemstatus konnte nicht geladen werden:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setCurrentUser(null);
    setCurrentView('dashboard');
  };

  const handleClearSearchHistory = async () => {
    if (!window.confirm('Suchverlauf löschen? PENDING und REJECTED Firmen werden gelöscht. GENEHMIGTE bleiben erhalten!')) {
      return;
    }

    try {
      const response = await axios.post(`${API}/clear-search-history`);
      
      toast.success('✅ Suchverlauf gelöscht!', {
        description: response.data.message
      });
      
      // Reset search stats
      const resetStats = { found: 0, filtered: 0, approved: 0 };
      setSearchStats(resetStats);
      localStorage.setItem('searchStats', JSON.stringify(resetStats));
      
      // Refresh data
      await fetchCompanies();
      await fetchStats();
    } catch (error) {
      console.error('Clear history error:', error);
      toast.error('❌ Fehler beim Löschen');
    }
  };

  // If not logged in, show landing page
  if (!currentUser) {
    return <LandingPage onLoginSuccess={handleLogin} />;
  }

  const handleSearchWithCities = async (cityNames, radiusValue) => {
    setLoading(true);
    
    // Reset search stats at the start of each search
    const initialStats = { found: 0, filtered: 0, approved: 0 };
    setSearchStats(initialStats);
    localStorage.setItem('searchStats', JSON.stringify(initialStats));
    
    const actualRadius = radiusValue !== undefined ? radiusValue : radius;
    
    console.log('🔍 SEARCH DEBUG:', {
      cityNames,
      radiusValue,
      actualRadius,
      defaultRadius: radius
    });
    
    toast.info('🗺️ Suche gestartet...', {
      description: actualRadius > 0
        ? `${cityNames.length} Städte mit ${actualRadius}km Radius`
        : `${cityNames.length} Stadt/Städte (nur Stadt-Zentrum)`
    });

    try {
      const response = await axios.post(`${API}/search-companies`, {
        query: searchQuery,
        locations: cityNames,
        radius_km: actualRadius,
        multi_query: multiQuery
      });

      const searchId = response.data.search_id;
      console.log('✅ SEARCH STARTED:', response.data);

      if (searchId) {
        // Poll progress every 2.5s until the background job finishes (no more timeouts)
        let refreshCounter = 0;
        await new Promise((resolve) => {
          const interval = setInterval(async () => {
            try {
              const prog = await axios.get(`${API}/search-progress/${searchId}`);
              const p = prog.data;
              const newStats = {
                found: p.found || 0,
                filtered: p.filtered || 0,
                approved: p.saved || 0
              };
              setSearchStats(newStats);
              localStorage.setItem('searchStats', JSON.stringify(newStats));

              refreshCounter += 1;
              if (refreshCounter % 8 === 0) {
                fetchCompanies();
                fetchStats();
              }

              if (p.status === 'DONE' || p.status === 'ERROR') {
                clearInterval(interval);
                if (p.status === 'DONE') {
                  toast.success('✅ Suche abgeschlossen!', { description: p.message });
                } else {
                  toast.error('Suche fehlgeschlagen', { description: p.message });
                }
                resolve();
              }
            } catch (pollError) {
              console.error('Progress poll failed:', pollError);
              clearInterval(interval);
              resolve();
            }
          }, 2500);
        });
      } else {
        toast.success('✅ Suche abgeschlossen!', {
          description: response.data.message
        });
      }

      // Refresh data
      await fetchCompanies();
      await fetchStats();
    } catch (error) {
      console.error('❌ SEARCH ERROR:', error);
      toast.error('Suche fehlgeschlagen', {
        description: error.response?.data?.detail || 'Ein Fehler ist aufgetreten'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen app-container">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="modern-header">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-1.5 sm:p-2 rounded-lg">
                <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-white" data-testid="app-title">RYAN-BOT</h1>
                <p className="text-xs sm:text-sm text-gray-300 hidden xs:block">Autonomes Lead-Finding-System</p>
              </div>
            </div>
            
            <div className="flex items-center gap-1.5 sm:gap-3">
              <div className="flex items-center gap-2 mr-2">
                <span className="text-sm text-gray-200 hidden sm:inline">
                  {currentUser.username}
                </span>
                {currentUser.role === 'admin' && (
                  <Badge variant="secondary" className="hidden sm:inline-flex">
                    Admin
                  </Badge>
                )}
              </div>
              
              {(currentUser.role === 'admin' || currentUser.role === 'sub_admin') && (
                <>
                  <Button
                    variant={currentView === 'admin-users' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCurrentView(currentView === 'dashboard' ? 'admin-users' : 'dashboard')}
                    className={currentView === 'admin-users' ? '' : 'bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white'}
                  >
                    {currentView === 'dashboard' ? (
                      <>
                        <Users className="h-4 w-4 mr-1" />
                        <span className="hidden sm:inline">Benutzer</span>
                      </>
                    ) : (
                      <>
                        <Building2 className="h-4 w-4 mr-1" />
                        <span className="hidden sm:inline">Dashboard</span>
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedList('contact-messages')}
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white"
                  >
                    📬 <span className="hidden sm:inline ml-1">Nachrichten</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedList('blacklist')}
                    data-testid="open-blacklist-btn"
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white"
                  >
                    🚫 <span className="hidden sm:inline ml-1">Blacklist</span>
                  </Button>
                  {currentUser.role === 'admin' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleClearSearchHistory}
                      className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 border-orange-300"
                    >
                      🗑️ <span className="hidden sm:inline ml-1">Verlauf löschen</span>
                    </Button>
                  )}
                </>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {currentView === 'admin-users' ? (
          <AdminUsers />
        ) : (
          <>
        {/* Stats Cards - Clickable Digital Style */}
        {!selectedList && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 sm:gap-6 mb-8">
            {/* Total */}
            <Card 
              className="stat-card glass-card cursor-pointer hover:scale-105 transition-transform" 
              data-testid="stat-total"
            >
              <CardContent className="pt-6 text-center">
                <div className="digital-counter" style={{background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>{stats.total}</div>
                <div className="stat-label mt-2">Gesamt</div>
              </CardContent>
            </Card>

            {/* Approved */}
            <Card 
              className="stat-card glass-card cursor-pointer hover:scale-105 transition-transform" 
              data-testid="stat-approved"
              onClick={() => setSelectedList('approved')}
            >
              <CardContent className="pt-6 text-center">
                <div className="digital-counter" style={{background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>{stats.approved}</div>
                <div className="stat-label mt-2">Genehmigt</div>
              </CardContent>
            </Card>

            {/* Pending */}
            <Card 
              className="stat-card glass-card cursor-pointer hover:scale-105 transition-transform" 
              data-testid="stat-pending"
              onClick={() => setSelectedList('pending')}
            >
              <CardContent className="pt-6 text-center">
                <div className="digital-counter" style={{background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>{stats.pending}</div>
                <div className="stat-label mt-2">Ausstehend</div>
              </CardContent>
            </Card>

            {/* Rejected */}
            <Card 
              className="stat-card glass-card cursor-pointer hover:scale-105 transition-transform" 
              data-testid="stat-rejected"
              onClick={() => setSelectedList('rejected')}
            >
              <CardContent className="pt-6 text-center">
                <div className="digital-counter" style={{background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>{stats.rejected}</div>
                <div className="stat-label mt-2">Abgelehnt</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Show selected list or dashboard */}
        {selectedList === 'approved' && (
          <ApprovedList 
            companies={companies}
            onBack={() => setSelectedList(null)}
            currentUser={currentUser}
            onRefresh={() => {
              fetchCompanies();
              fetchStats();
            }}
            onDelete={async (companyIds) => {
              try {
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/companies/bulk-delete`, {
                  method: 'DELETE',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                  },
                  body: JSON.stringify(companyIds)
                });
                
                if (response.ok) {
                  const data = await response.json();
                  toast.success(`${data.deleted_count} Firmen gel\u00f6scht!`);
                  fetchCompanies();
                  fetchStats();
                } else {
                  toast.error('Fehler beim L\u00f6schen');
                }
              } catch (error) {
                console.error('Delete error:', error);
                toast.error('Fehler beim L\u00f6schen');
              }
            }}
          />
        )}

        {selectedList === 'pending' && (
          <PendingList 
            onBack={() => setSelectedList(null)}
            onDecisionComplete={() => {
              fetchCompanies();
              fetchStats();
            }}
            currentUser={currentUser}
          />
        )}

        {selectedList === 'rejected' && (
          <RejectedList 
            companies={companies}
            onBack={() => setSelectedList(null)}
            onRestore={() => {
              fetchCompanies();
              fetchStats();
            }}
          />
        )}

        {selectedList === 'contact-messages' && (
          <ContactMessages 
            onBack={() => setSelectedList(null)}
          />
        )}

        {selectedList === 'blacklist' && (
          <BlacklistAdmin
            onBack={() => setSelectedList(null)}
          />
        )}

        {/* Search Section */}
        {!selectedList && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Unternehmen suchen</CardTitle>
            <CardDescription>
              <div className="flex flex-wrap items-center gap-3 text-sm font-medium">
                {loading && (
                  <span className="text-blue-600 animate-pulse">🔍 Suche läuft...</span>
                )}
                <span className={searchStats.approved > 0 ? "text-green-600" : "text-gray-400"}>
                  ✓ {searchStats.approved} Firmen gesendet
                </span>
                <span className={searchStats.filtered > 0 ? "text-orange-500" : "text-gray-400"}>
                  ⊘ {searchStats.filtered} gefiltert
                </span>
                <span className={searchStats.found > 0 ? "text-blue-500" : "text-gray-400"}>
                  🔍 {searchStats.found} gefunden
                </span>
              </div>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search Query */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Suchanfrage</label>
              <Input
                data-testid="search-input"
                placeholder="z.B.: Produktionslogistik Werkslogistik"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
              />
              <label className="flex items-center gap-2 mt-2 text-sm text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={multiQuery}
                  onChange={(e) => setMultiQuery(e.target.checked)}
                  data-testid="multi-query-checkbox"
                  className="h-4 w-4 accent-purple-600"
                />
                <span>
                  🔍 <strong>Erweiterte Suche</strong> — sucht zusätzlich: Spedition, Lagerlogistik, Kontraktlogistik, Transportunternehmen, Fulfillment
                  <span className="text-amber-600 ml-1">(~6x SerpAPI-Verbrauch)</span>
                </span>
              </label>
            </div>

            {/* Radius Search - Inline */}
            <div className="border-2 border-purple-300 rounded-lg p-4 bg-gradient-to-br from-purple-50 to-white">
              <h3 className="font-semibold text-base text-purple-900 mb-3 flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Radius-basierte Suche
              </h3>
              <RadiusSearchInline 
                onSearch={(cityNames) => {
                  handleSearchWithCities(cityNames);
                }}
              />
            </div>
          </CardContent>
        </Card>
        )}
        </>
        )}
      </main>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-6 mt-12 text-center text-sm text-slate-500">
        <p>🤖 RYAN-BOT - Autonomes Lead-Finding-System per Telegram gesteuert</p>
      </footer>
    </div>
  );
}

export default App;
